import { useCallback } from 'react';
import { FilterDocuments } from 'shared/constants';
import {
  DocumentOrganizationKeys,
  FilterDocument,
  FilterDocumentDateFilter,
  FilterDocumentDurationFilter,
  FilterOptionKeys,
} from 'shared/types/filter-document';
import { ID } from 'shared/types/id';
import { noTagId } from 'shared/types/orderable-section';
import { GroupByMode, SortingMode } from 'shared/types/sorting-mode';
import { TaskPriorityOptions, TaskType } from 'shared/types/task-base';

export const useOnFilterOption = <
  GBM extends GroupByMode,
  SM extends SortingMode,
  F extends FilterOptionKeys,
>(
  filterDocument: FilterDocument<GBM, SM, F>,
  defaultFilterDocument: FilterDocument<GBM, SM, F> = {},
  callback?: (updatedDocument: FilterDocument<GBM, SM, F>) => void,
) =>
  useCallback(
    (options: {
      id: ID;
      value?: string | string[];
      durationFilter?: FilterDocumentDurationFilter | null;
      dateFilter?: FilterDocumentDateFilter | null;
    }) => {
      // no callback means no changes have to be calculated
      if (!callback) {
        return;
      }

      const { id, value: val, durationFilter, dateFilter } = options;
      const valArray = !val ? null : Array.isArray(val) ? val : [val];
      const updatedDocument = structuredClone(filterDocument) as FilterDocument<
        GBM,
        SM
      >;

      if (id === DocumentOrganizationKeys.SortingMode) {
        updatedDocument[DocumentOrganizationKeys.SortingMode] =
          (valArray?.[0] as SM | undefined) ?? null;
      }

      if (id === DocumentOrganizationKeys.GroupByMode) {
        updatedDocument[DocumentOrganizationKeys.GroupByMode] =
          (valArray?.[0] as GBM | undefined) ?? null;
      }

      if (id === FilterDocuments.ResetFilterDocumentID) {
        updatedDocument[DocumentOrganizationKeys.Filters] =
          defaultFilterDocument[DocumentOrganizationKeys.Filters];
      }

      if (id === FilterOptionKeys.LifeArea) {
        updatedDocument[DocumentOrganizationKeys.Filters] ??= {};

        updatedDocument[DocumentOrganizationKeys.Filters][
          FilterOptionKeys.LifeArea
        ] = valArray?.length
          ? {
              include: valArray,
            }
          : null;
      }
      if (id === FilterOptionKeys.Priority) {
        updatedDocument[DocumentOrganizationKeys.Filters] ??= {};

        updatedDocument[DocumentOrganizationKeys.Filters][
          FilterOptionKeys.Priority
        ] = valArray?.length
          ? {
              include: valArray as TaskPriorityOptions[],
            }
          : null;
      }
      if (id === FilterOptionKeys.Type) {
        updatedDocument[DocumentOrganizationKeys.Filters] ??= {};

        updatedDocument[DocumentOrganizationKeys.Filters][
          FilterOptionKeys.Type
        ] = valArray?.length
          ? {
              include: valArray as TaskType[],
            }
          : null;
      }

      if (id === FilterOptionKeys.Tag) {
        updatedDocument[DocumentOrganizationKeys.Filters] ??= {};

        const noTag = valArray?.includes(noTagId) ?? false;
        const include = valArray?.filter((val) => val !== noTagId) ?? [];

        updatedDocument[DocumentOrganizationKeys.Filters][
          FilterOptionKeys.Tag
        ] = !valArray?.length
          ? null
          : {
              include: include.length ? (include as string[]) : null,
              noTag: noTag ? true : null,
            };
      }

      if (id === FilterOptionKeys.Goal) {
        updatedDocument[DocumentOrganizationKeys.Filters] ??= {};
        const currentAvailable =
          updatedDocument[DocumentOrganizationKeys.Filters]?.[
            FilterOptionKeys.Goal
          ]?.availableOptions;

        updatedDocument[DocumentOrganizationKeys.Filters][
          FilterOptionKeys.Goal
        ] = valArray?.length
          ? {
              include: valArray,
              ...(currentAvailable
                ? { availableOptions: currentAvailable }
                : {}),
            }
          : null;
      }

      if (id === FilterOptionKeys.Duration) {
        updatedDocument[DocumentOrganizationKeys.Filters] ??= {};

        updatedDocument[DocumentOrganizationKeys.Filters][
          FilterOptionKeys.Duration
        ] = durationFilter ? durationFilter : null;
      }

      if (id === FilterOptionKeys.Date) {
        updatedDocument[DocumentOrganizationKeys.Filters] ??= {};

        updatedDocument[DocumentOrganizationKeys.Filters][
          FilterOptionKeys.Date
        ] = dateFilter ? dateFilter : null;
      }

      if (id === FilterOptionKeys.Deadline) {
        updatedDocument[DocumentOrganizationKeys.Filters] ??= {};

        updatedDocument[DocumentOrganizationKeys.Filters][
          FilterOptionKeys.Deadline
        ] = dateFilter ? dateFilter : null;
      }

      if (id === FilterOptionKeys.GoalActive) {
        updatedDocument[DocumentOrganizationKeys.Filters] ??= {};

        updatedDocument[DocumentOrganizationKeys.Filters][
          FilterOptionKeys.GoalActive
        ] = dateFilter ? dateFilter : null;
      }

      callback?.(updatedDocument as FilterDocument<GBM, SM, F>);
    },
    [callback, defaultFilterDocument, filterDocument],
  );
